import React from "react";
// src/pages/__preview.tsx
import { useMemo, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { getOrCreateActiveSessionId } from "@/lib/activeSessionId";

const PING_INTERVAL_MS = 250;
const PING_TIMEOUT_MS = 8000;
const TELEMETRY_KEY_PREFIX = "dayone:sandbox:telemetry:";

export default function PreviewPage() {
  const [sp] = useSearchParams();
  const sessionId = useMemo(
    () => getOrCreateActiveSessionId(sp.get("sessionId") || null),
    [sp]
  );
  const cacheBust = useMemo(() => sp.get("t") ?? sp.get("k") ?? String(Date.now()), [sp]);
  const debug = sp.get("debug");
  const iframeSrc = useMemo(() => {
    const q = `sessionId=${encodeURIComponent(sessionId)}&t=${encodeURIComponent(cacheBust)}${debug === "1" ? "&debug=1" : ""}`;
    return `/__render?${q}`;
  }, [sessionId, cacheBust, debug]);
  const renderSrcRef = useRef(iframeSrc);
  renderSrcRef.current = iframeSrc;

  const renderIframeRef = useRef<HTMLIFrameElement | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pingStartRef = useRef<number>(0);

  const sendPing = useCallback(() => {
    const iframe = renderIframeRef.current;
    const win = iframe?.contentWindow;
    if (debug === "1") {
      console.log("[__preview] PING ->", sessionId, !!win, "renderSrc=", renderSrcRef.current);
    }
    if (win) {
      win.postMessage({ type: "DAYONE_PREVIEW_PING", sessionId, ts: Date.now() }, "*");
    }
  }, [sessionId, debug]);

  const sendHello = useCallback(
    (nonce: string) => {
      const win = renderIframeRef.current?.contentWindow;
      if (debug === "1") {
        console.log("[__preview] HELLO -> relay to render", sessionId, nonce, !!win);
      }
      if (win) {
        win.postMessage({ type: "DAYONE_HOST_HELLO", sessionId, nonce, ts: Date.now() }, "*");
      }
    },
    [sessionId, debug]
  );

  const stopPinging = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const data = e.data;
      if (!data) return;
      const fromRenderIframe = renderIframeRef.current?.contentWindow && e.source === renderIframeRef.current.contentWindow;
      const fromParent = e.source === window.parent;

      if (data.type === "DAYONE_HOST_HELLO" && fromParent && data.sessionId && data.nonce) {
        sendHello(data.nonce);
        return;
      }

      if (data.type === "DAYONE_RENDER_ACK" && fromRenderIframe) {
        if (debug === "1") {
          console.log("[__preview] ACK <-", data.sessionId, data.nonce, "mismatch:", data.sessionId !== sessionId, "rendered:", data.rendered);
        }
        stopPinging();
        window.parent.postMessage(data, "*");
        return;
      }

      if (data.type !== "DAYONE_PREVIEW_TELEMETRY") return;
      if (debug === "1") {
        console.log("[__preview] TELEMETRY <-", data.sessionId, "fromRenderIframe:", !!fromRenderIframe, "mismatch:", data.mismatch, data.pingSessionId);
      }
      if (!fromRenderIframe) return;
      stopPinging();
      const sid = data.sessionId ?? sessionId;
      if (sid) {
        try {
          localStorage.setItem(TELEMETRY_KEY_PREFIX + sid, JSON.stringify({ ...data, ts: Date.now(), source: "__preview" }));
          if (debug === "1") console.log("[__preview] telemetry localStorage", sid, data.rendered, data.error);
        } catch (_) {}
      }
      window.parent.postMessage(data, "*");
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [sessionId, stopPinging, debug, sendHello]);

  useEffect(() => {
    return () => stopPinging();
  }, [stopPinging]);

  const startPingLoop = useCallback(() => {
    stopPinging();
    pingStartRef.current = Date.now();
    sendPing();
    pingIntervalRef.current = setInterval(() => {
      if (Date.now() - pingStartRef.current > PING_TIMEOUT_MS) {
        stopPinging();
        return;
      }
      sendPing();
    }, PING_INTERVAL_MS);
  }, [sendPing, stopPinging]);

  const handleIframeLoad = useCallback(() => {
    sendPing();
    startPingLoop();
  }, [sendPing, startPingLoop]);

  // Kill body scroll in the __preview iframe — the inner iframe must fill 100% of the viewport
  useEffect(() => {
    const s = document.documentElement.style;
    const b = document.body.style;
    s.height = "100%";
    s.overflow = "hidden";
    s.margin = "0";
    b.height = "100%";
    b.overflow = "hidden";
    b.margin = "0";
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
  }, []);

  if (debug === "1") {
    console.log("[__preview] mounted", { url: typeof window !== "undefined" ? window.location.href : "", sessionId, cacheBust, iframeSrc });
  }

  return (
    <div style={{ position: "fixed", inset: 0, width: "100%", height: "100%", overflow: "hidden" }}>
      <iframe
        ref={renderIframeRef}
        key={cacheBust}
        title="DayOne Preview"
        style={{ border: 0, width: "100%", height: "100%" }}
        src={iframeSrc}
        sandbox="allow-scripts allow-forms allow-modals allow-popups allow-downloads allow-same-origin"
        onLoad={handleIframeLoad}
      />
    </div>
  );
}
